import { useCallback, useEffect, useMemo, useState } from 'react'
import { ExportModal } from '@/components/export/ExportModal'
import { SessionRowActions } from '@/components/sessions/SessionRowActions'
import { sessionApi } from '@/lib/api/sessions'
import { Button } from '@/components/ui/button'
import type { ChatSession } from '@/types/chat'

export function SessionsPage() {
  const [sessions, setSessions] = useState<ChatSession[]>([])
  const [statusFilter, setStatusFilter] = useState('all')
  const [exportSession, setExportSession] = useState<ChatSession | null>(null)
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [batchExportOpen, setBatchExportOpen] = useState(false)
  const [busyId, setBusyId] = useState<string | null>(null)

  const loadSessions = useCallback(() => {
    sessionApi
      .listSessions({ status: statusFilter })
      .then((result) => setSessions(result.items))
      .catch(() => setSessions([]))
  }, [statusFilter])

  useEffect(() => {
    loadSessions()
  }, [loadSessions])

  useEffect(() => {
    setSelectedIds((current) => current.filter((id) => sessions.some((session) => session.id === id)))
  }, [sessions])

  const selectedCount = selectedIds.length
  const batchTitle = useMemo(
    () => (selectedCount === 1 ? sessions.find((item) => item.id === selectedIds[0])?.title : `${selectedCount} sessions`),
    [selectedCount, selectedIds, sessions],
  )

  const toggleSelected = (sessionId: string) => {
    setSelectedIds((current) =>
      current.includes(sessionId) ? current.filter((id) => id !== sessionId) : [...current, sessionId],
    )
  }

  return (
    <div className="flex h-full flex-col" data-testid="sessions-page">
      <div className="sticky top-0 z-10 flex flex-col gap-3 border-b border-border bg-background/95 px-4 py-3 backdrop-blur sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-lg font-semibold">Sessions</h1>
          <p className="text-sm text-muted-foreground">Open, duplicate, or export chat sessions</p>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          {selectedCount > 0 ? (
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => setBatchExportOpen(true)}
              data-testid="sessions-batch-export"
            >
              Export selected ({selectedCount})
            </Button>
          ) : null}
          <select
            className="min-h-11 w-full rounded-md border border-input bg-background px-3 py-2 text-sm sm:w-auto"
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
            data-testid="sessions-status-filter"
          >
            <option value="all">All</option>
            <option value="open">Open</option>
            <option value="closed">Closed</option>
            <option value="archived">Archived</option>
          </select>
        </div>
      </div>

      <ul className="divide-y divide-border overflow-auto">
        {sessions.map((session) => (
          <li key={session.id} className="px-4 py-4" data-testid={`session-row-${session.id}`}>
            <div className="flex flex-col gap-3">
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  className="mt-1 h-4 w-4"
                  checked={selectedIds.includes(session.id)}
                  onChange={() => toggleSelected(session.id)}
                  aria-label={`Select ${session.title}`}
                  data-testid={`session-select-${session.id}`}
                />
                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-medium">{session.title}</p>
                      <p className="truncate text-xs text-muted-foreground">{session.modelName}</p>
                    </div>
                    <span
                      className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-xs capitalize"
                      data-testid={`session-status-${session.id}`}
                    >
                      {session.status}
                    </span>
                  </div>
                </div>
              </div>

              <SessionRowActions
                session={session}
                busy={busyId === session.id}
                onExport={() => setExportSession(session)}
                onReload={loadSessions}
                onBusyChange={(busy) => setBusyId(busy ? session.id : null)}
              />
            </div>
          </li>
        ))}
      </ul>

      {exportSession ? (
        <ExportModal
          open
          onClose={() => setExportSession(null)}
          exportType="session"
          sourceId={exportSession.id}
          title={exportSession.title}
        />
      ) : null}

      {batchExportOpen ? (
        <ExportModal
          open
          onClose={() => {
            setBatchExportOpen(false)
            setSelectedIds([])
          }}
          exportType="session"
          sourceIds={selectedIds}
          title={batchTitle ?? 'Selected sessions'}
        />
      ) : null}
    </div>
  )
}
